import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaSearch,
  FaTimes,
  FaBuilding,
  FaHome,
  FaExclamationTriangle,
  FaKeyboard,
  FaArrowRight,
  FaFileInvoiceDollar,
  FaTruck,
  FaBullhorn,
  FaUsers,
} from "react-icons/fa";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { colors } from "../utils/designTokens";
import { tolerantId, fetchUsersByUserIds } from "../utils/firestoreHelpers";
import { bidCanonico } from "../utils/consultasEdificio";

// ─── Constantes ─────────────────────────────────────────────────────────────

const MAX_PER_CATEGORY = 5;

// Mapa de keywords para navegacion rapida a pantallas
const SCREEN_KEYWORDS = [
  { keywords: ["inicio", "dashboard", "home"], label: "Inicio", action: "homeadmin" },
  { keywords: ["mis edificios", "edificios"], label: "Mis Edificios", action: "edificios" },
  { keywords: ["cobranza", "cobros", "recaudacion"], label: "Cobranza", action: "cobranzaadmin" },
  { keywords: ["gastos", "pagos", "facturas"], label: "Gastos", action: "pagosedificio" },
  { keywords: ["estado de cuenta"], label: "Estado de Cuenta", action: "estadocuentaedificio" },
  { keywords: ["informe", "informes"], label: "Informe de Gastos", action: "informegastos" },
  { keywords: ["cierre de mes", "cierre", "liquidacion"], label: "Cierre de Mes", action: "cierredemes" },
  { keywords: ["criterios", "division de gastos"], label: "Criterios (en Editar Edificio > Finanzas)", action: "edificios" },
  { keywords: ["multas"], label: "Multas", action: "multasadmin" },
  { keywords: ["incidencias", "reclamos"], label: "Incidencias", action: "incidenciasedificio" },
  { keywords: ["novedades", "anuncios", "noticias"], label: "Novedades", action: "novedadesedificio" },
  { keywords: ["proveedores"], label: "Proveedores", action: "proveedoresedificio" },
  { keywords: ["obras", "proyectos"], label: "Obras y Proyectos", action: "proyectosadmin" },
  { keywords: ["revisiones", "inspecciones"], label: "Revisiones", action: "revisionesadmin" },
  { keywords: ["documentos", "documentacion"], label: "Documentos", action: "documentacionadmin" },
  { keywords: ["unidades"], label: "Unidades (en Editar Edificio > Unidades)", action: "edificios" },
  { keywords: ["usuarios", "gestion usuarios"], label: "Usuarios", action: "gestionusuarios" },
  { keywords: ["alta residente", "onboarding", "invitar residente"], label: "Alta de Residente", action: "onboardingresidente" },
  { keywords: ["asambleas", "reuniones", "actas", "convocatoria"], label: "Asambleas", action: "asambleas" },
  { keywords: ["amenities", "reservas"], label: "Amenities", action: "reservasamenities" },
  { keywords: ["asistencia", "asistencia empleados"], label: "Asistencia", action: "asistenciaempleados" },
  { keywords: ["tareas"], label: "Tareas", action: "tareasadmin" },
  { keywords: ["turnos"], label: "Turnos", action: "turnosadmin" },
  { keywords: ["chat", "chat empleados"], label: "Chat Empleados", action: "chatadmin" },
  { keywords: ["licencias", "solicitudes"], label: "Licencias", action: "solicitudesadmin" },
  { keywords: ["liquidaciones", "recibos de sueldo"], label: "Liquidaciones", action: "recibosadmin" },
];

// Orden de categorias en el dropdown
const CATEGORY_ORDER = [
  "navigation",
  "building",
  "unit",
  "expense",
  "incident",
  "supplier",
  "novedad",
  "resident",
];

// Config de cada categoria
const CATEGORY_CONFIG = {
  navigation: { label: "Navegación rápida", icon: FaArrowRight, color: colors.primary },
  building: { label: "Edificios", icon: FaBuilding, color: colors.primary },
  unit: { label: "Unidades", icon: FaHome, color: colors.success },
  expense: { label: "Gastos", icon: FaFileInvoiceDollar, color: colors.warning },
  incident: { label: "Incidencias", icon: FaExclamationTriangle, color: colors.warning },
  supplier: { label: "Proveedores", icon: FaTruck, color: "#8b5cf6" },
  novedad: { label: "Novedades", icon: FaBullhorn, color: colors.info },
  resident: { label: "Residentes", icon: FaUsers, color: colors.success },
};

// ─── Helpers de busqueda ────────────────────────────────────────────────────

// Tier 1: Navegacion rapida por keywords (sincrono)
const searchNavigation = (termLower) => {
  const found = [];
  for (const entry of SCREEN_KEYWORDS) {
    const match = entry.keywords.some(
      (kw) => kw.includes(termLower) || termLower.includes(kw)
    );
    if (match) {
      found.push({
        type: "navigation",
        icon: FaArrowRight,
        label: entry.label,
        sublabel: "Ir a esta sección",
        action: entry.action,
        color: colors.primary,
      });
    }
  }
  return found.slice(0, MAX_PER_CATEGORY);
};

// Tier 1: Edificios en memoria (sincrono)
const searchBuildings = (termLower, buildings) => {
  if (!buildings?.length) return [];
  const found = [];
  for (const b of buildings) {
    const match =
      (b.name && b.name.toLowerCase().includes(termLower)) ||
      (b.address && b.address.toLowerCase().includes(termLower)) ||
      (b.city && b.city.toLowerCase().includes(termLower));
    if (match) {
      found.push({
        type: "building",
        icon: FaBuilding,
        label: b.name || `Edificio ${b.buildingid}`,
        sublabel: b.address || b.city || "",
        action: "editaredificio",
        color: colors.primary,
        buildingData: {
          id: b.id,
          buildingid: b.buildingid,
          name: b.name,
          address: b.address,
          city: b.city,
          department: b.department,
          country: b.country,
        },
      });
    }
  }
  return found.slice(0, MAX_PER_CATEGORY);
};

// Agrupar buildingIds en lotes para queries con tolerantId
const batchBuildingIds = (ids) => {
  const batches = [];
  for (let i = 0; i < ids.length; i += 10) {
    batches.push(ids.slice(i, i + 10));
  }
  return batches;
};

// Tier 2: Queries Firestore en paralelo
const searchCollections = async (termLower, buildingIds, buildings) => {
  if (!buildingIds.length) return { units: [], incidents: [], expenses: [], novedades: [], apartmentDocs: [] };

  const batches = batchBuildingIds(buildingIds);
  const allUnits = [];
  const allIncidents = [];
  const allExpenses = [];
  const allNovedades = [];
  const allApartmentDocs = [];

  for (const batch of batches) {
    const tolerantBatch = batch.flatMap((id) => tolerantId(id));

    // Consultar las 4 colecciones en paralelo
    const [aptsSnap, incSnap, expSnap, novSnap] = await Promise.all([
      getDocs(query(collection(db, "apartments"), where("building", "in", tolerantBatch))),
      getDocs(query(collection(db, "incidents"), where("buildingid", "in", tolerantBatch))),
      getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantBatch))),
      getDocs(query(collection(db, "novedades"), where("buildingid", "in", tolerantBatch))),
    ]);

    // Apartments
    aptsSnap.forEach((doc) => {
      const d = doc.data();
      allApartmentDocs.push(d);
      const num = String(d.number || d.apartmentNumber || "").toLowerCase();
      const floor = String(d.floor || "").toLowerCase();
      if (num.includes(termLower) || floor.includes(termLower)) {
        const bld = buildings?.find((b) => Number(b.buildingid) === Number(d.building));
        allUnits.push({
          type: "unit",
          icon: FaHome,
          label: `Unidad ${d.number || d.apartmentNumber || doc.id}`,
          sublabel: bld ? bld.name : `Edificio ${d.building}`,
          action: "residentes",
          color: colors.success,
        });
      }
    });

    // Incidents
    incSnap.forEach((doc) => {
      const d = doc.data();
      const desc = String(d.description || d.descripcion || "").toLowerCase();
      const issue = String(d.issue || "").toLowerCase();
      const status = String(d.status || d.state || "").toLowerCase();
      if (desc.includes(termLower) || issue.includes(termLower) || status.includes(termLower)) {
        allIncidents.push({
          type: "incident",
          icon: FaExclamationTriangle,
          label: d.title || d.titulo || d.issue || `Incidencia`,
          sublabel: d.description || d.descripcion || "",
          action: "incidenciasedificio",
          color: colors.warning,
        });
      }
    });

    // Expenses
    expSnap.forEach((doc) => {
      const d = doc.data();
      const desc = String(d.description || "").toLowerCase();
      const receipt = String(d.receiptNumber || "").toLowerCase();
      const amount = String(d.amount || "");
      if (desc.includes(termLower) || receipt.includes(termLower) || amount.includes(termLower)) {
        allExpenses.push({
          type: "expense",
          icon: FaFileInvoiceDollar,
          label: d.description || `Gasto #${d.receiptNumber || doc.id}`,
          sublabel: d.amount ? `$${Number(d.amount).toLocaleString("es-UY")}` : "",
          action: "pagosedificio",
          color: colors.warning,
        });
      }
    });

    // Novedades
    novSnap.forEach((doc) => {
      const d = doc.data();
      const title = String(d.title || "").toLowerCase();
      const desc = String(d.description || "").toLowerCase();
      if (title.includes(termLower) || desc.includes(termLower)) {
        allNovedades.push({
          type: "novedad",
          icon: FaBullhorn,
          label: d.title || "Novedad",
          sublabel: d.description || "",
          action: "novedadesedificio",
          color: colors.info,
        });
      }
    });
  }

  return {
    units: allUnits.slice(0, MAX_PER_CATEGORY),
    incidents: allIncidents.slice(0, MAX_PER_CATEGORY),
    expenses: allExpenses.slice(0, MAX_PER_CATEGORY),
    novedades: allNovedades.slice(0, MAX_PER_CATEGORY),
    apartmentDocs: allApartmentDocs,
  };
};

// Tier 3: Proveedores (requiere join supplierbuilding → supplier)
const searchSuppliersData = async (termLower, buildingIds) => {
  if (!buildingIds.length) return [];

  const batches = batchBuildingIds(buildingIds);
  const supplierIdSet = new Set();

  // Paso 1: Obtener relaciones proveedor-edificio
  for (const batch of batches) {
    const tolerantBatch = batch.flatMap((id) => tolerantId(id));
    const snap = await getDocs(
      query(collection(db, "supplierbuilding"), where("buildingId", "in", tolerantBatch))
    );
    snap.docs.forEach((d) => {
      const data = d.data();
      if (data.supplierId) supplierIdSet.add(String(data.supplierId));
    });
  }

  const supplierIds = [...supplierIdSet];
  if (!supplierIds.length) return [];

  // Paso 2: Obtener datos de proveedores en lotes de 30
  const allSuppliers = [];
  for (let i = 0; i < supplierIds.length; i += 30) {
    const batch = supplierIds.slice(i, i + 30);
    const snap = await getDocs(
      query(collection(db, "supplier"), where("supplierId", "in", batch))
    );
    snap.docs.forEach((d) => allSuppliers.push({ id: d.id, ...d.data() }));
  }

  // Paso 3: Filtrar por termino
  return allSuppliers
    .filter((s) => {
      const name = (s.name || "").toLowerCase();
      const rut = (s.rut || "").toLowerCase();
      const type = (s.type || "").toLowerCase();
      return name.includes(termLower) || rut.includes(termLower) || type.includes(termLower);
    })
    .slice(0, MAX_PER_CATEGORY)
    .map((s) => ({
      type: "supplier",
      icon: FaTruck,
      label: s.name || `Proveedor ${s.supplierId}`,
      sublabel: [s.type, s.rut].filter(Boolean).join(" · "),
      action: "proveedoresedificio",
      color: "#8b5cf6",
    }));
};

// Tier 3: Residentes (reutiliza apartments de Tier 2). C10 (reglas 0D): el
// staff lee fichas ajenas solo con el edificio en la consulta (accessBids), así
// que los ids se agrupan por el edificio de cada unidad y se piden edificio por
// edificio (fetchUsersByUserIds con el edificio). Un edificio que falla no se
// lleva los resultados de los demás.
const searchResidentsData = async (termLower, apartmentDocs) => {
  const idsPorEdificio = new Map();
  apartmentDocs.forEach((apt) => {
    const bid = bidCanonico(apt.building);
    if (!bid) return;
    if (!idsPorEdificio.has(bid)) idsPorEdificio.set(bid, new Set());
    const ids = idsPorEdificio.get(bid);
    if (apt.owner) ids.add(String(apt.owner));
    if (apt.tenant) ids.add(String(apt.tenant));
  });

  const pedidos = [...idsPorEdificio].filter(([, ids]) => ids.size > 0);
  if (!pedidos.length) return [];

  const porEdificio = await Promise.all(
    pedidos.map(async ([bid, ids]) => {
      try {
        return await fetchUsersByUserIds([...ids], bid);
      } catch (err) {
        console.warn(`GlobalSearch: sin residentes del edificio ${bid}:`, err?.code || err?.message);
        return [];
      }
    })
  );
  // La misma ficha puede venir de dos edificios (una persona en ambos).
  const vistas = new Set();
  const users = porEdificio.flat().filter((u) => {
    const clave = u.id || `userid:${u.userid}`;
    if (vistas.has(clave)) return false;
    vistas.add(clave);
    return true;
  });

  // fetchUsersByUserIds completa name/email faltantes con "Sin nombre"/"Sin email":
  // no se buscan ni se muestran como si fueran datos.
  const real = (v, centinela) => (v && v !== centinela ? String(v) : "");
  return users
    .filter((u) => {
      const fullName = (u.NombreCompleto || real(u.name, "Sin nombre")).toLowerCase();
      const email = real(u.email, "Sin email").toLowerCase();
      return fullName.includes(termLower) || email.includes(termLower);
    })
    .slice(0, MAX_PER_CATEGORY)
    .map((u) => ({
      type: "resident",
      icon: FaUsers,
      label: u.NombreCompleto || real(u.name, "Sin nombre") || "Sin nombre",
      sublabel: real(u.email, "Sin email"),
      action: "residentes",
      color: colors.success,
    }));
};

// Ordenar y agrupar resultados segun CATEGORY_ORDER
const mergeResults = (allResults) => {
  const grouped = {};
  allResults.forEach((r) => {
    if (!grouped[r.type]) grouped[r.type] = [];
    if (grouped[r.type].length < MAX_PER_CATEGORY) grouped[r.type].push(r);
  });

  const ordered = [];
  CATEGORY_ORDER.forEach((cat) => {
    if (grouped[cat]?.length) {
      ordered.push(...grouped[cat]);
    }
  });
  return ordered;
};

// ─── Componente ─────────────────────────────────────────────────────────────

const GlobalSearch = ({ setSelected }) => {
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();

  // IDs de edificios a buscar (respeta filtro global)
  const getTargetBuildingIds = useCallback(() => {
    if (!buildings?.length) return [];
    if (selectedBuilding) {
      const id = Number(selectedBuilding.buildingid);
      return Number.isFinite(id) ? [id] : [];
    }
    return buildings.map((b) => Number(b.buildingid)).filter(Number.isFinite);
  }, [buildings, selectedBuilding]);

  // Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback(
    async (term) => {
      // Cancelar busqueda anterior
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!term.trim()) {
        setResults([]);
        setOpen(false);
        return;
      }

      const termLower = term.toLowerCase().trim();
      const buildingIds = getTargetBuildingIds();

      // ── TIER 1: Instantaneo ──
      const navResults = searchNavigation(termLower);
      const buildingResults = searchBuildings(termLower, buildings);
      const tier1 = mergeResults([...navResults, ...buildingResults]);

      setResults(tier1);
      setOpen(true);
      setLoading(true);
      setActiveIndex(-1);

      try {
        // ── TIER 2: Colecciones Firestore en paralelo ──
        const { units, incidents, expenses, novedades, apartmentDocs } =
          await searchCollections(termLower, buildingIds, buildings);

        if (controller.signal.aborted) return;

        const tier2 = mergeResults([
          ...navResults,
          ...buildingResults,
          ...units,
          ...incidents,
          ...expenses,
          ...novedades,
        ]);
        setResults(tier2);
        setOpen(tier2.length > 0 || tier1.length > 0);

        // ── TIER 3: Proveedores y residentes (joins) ──
        const [suppliers, residents] = await Promise.all([
          searchSuppliersData(termLower, buildingIds),
          searchResidentsData(termLower, apartmentDocs),
        ]);

        if (controller.signal.aborted) return;

        const allResults = mergeResults([
          ...navResults,
          ...buildingResults,
          ...units,
          ...incidents,
          ...expenses,
          ...novedades,
          ...suppliers,
          ...residents,
        ]);
        setResults(allResults);
        setOpen(allResults.length > 0);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("GlobalSearch error:", err);
        }
      }

      if (!controller.signal.aborted) {
        setLoading(false);
      }
    },
    [buildings, selectedBuilding, getTargetBuildingIds]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (result) => {
    // Si es un edificio, seleccionarlo antes de navegar a editar
    if (result.buildingData) {
      setSelectedBuilding(result.buildingData);
    }
    setSelected(result.action);
    setSearchText("");
    setResults([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setSearchText("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Renderizado de resultados agrupados
  let lastType = null;
  let flatIndex = -1;

  return (
    <div ref={containerRef} style={styles.container}>
      <div
        style={{
          ...styles.searchBox,
          borderColor: open ? colors.primary : "transparent",
          boxShadow: open ? `0 0 0 3px ${colors.primaryLighter}` : "none",
        }}
      >
        <FaSearch size={14} color={loading ? colors.primary : colors.gray400} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar en la plataforma..."
          value={searchText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          style={styles.input}
        />
        {searchText ? (
          <button style={styles.clearBtn} onClick={handleClear}>
            <FaTimes size={10} />
          </button>
        ) : (
          <span style={styles.shortcut}>
            <FaKeyboard size={10} style={{ opacity: 0.6 }} />
            <span>K</span>
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <div style={styles.dropdown}>
          {results.map((r, idx) => {
            flatIndex++;
            const showHeader = r.type !== lastType;
            lastType = r.type;
            const isActive = flatIndex === activeIndex;
            const currentIdx = flatIndex;
            const catConfig = CATEGORY_CONFIG[r.type];

            return (
              <React.Fragment key={`${r.type}-${idx}`}>
                {showHeader && catConfig && (
                  <div style={styles.categoryHeader}>
                    {catConfig.label}
                  </div>
                )}
                <div
                  style={{
                    ...styles.resultItem,
                    backgroundColor: isActive ? colors.primarySubtle : "transparent",
                  }}
                  onClick={() => handleSelect(r)}
                  onMouseEnter={(e) => {
                    setActiveIndex(currentIdx);
                    e.currentTarget.style.backgroundColor = colors.gray50;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isActive
                      ? colors.primarySubtle
                      : "transparent";
                  }}
                >
                  <div style={{ ...styles.resultIcon, backgroundColor: `${r.color}15` }}>
                    <r.icon size={14} color={r.color} />
                  </div>
                  <div style={styles.resultInfo}>
                    <span style={styles.resultLabel}>{r.label}</span>
                    {r.sublabel && (
                      <span style={styles.resultSublabel}>
                        {r.sublabel.length > 60
                          ? r.sublabel.slice(0, 60) + "\u2026"
                          : r.sublabel}
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <div style={styles.footer}>
            <span style={styles.footerText}>
              {loading
                ? "Buscando..."
                : `${results.length} resultado${results.length !== 1 ? "s" : ""}`}
            </span>
            <span style={styles.footerHint}>
              \u2191\u2193 navegar \u00b7 Enter seleccionar \u00b7 Esc cerrar
            </span>
          </div>
        </div>
      )}

      {open && results.length === 0 && searchText.trim() && !loading && (
        <div style={styles.dropdown}>
          <div style={styles.noResults}>
            <FaSearch size={16} color={colors.gray300} />
            <span>No se encontraron resultados</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Estilos ────────────────────────────────────────────────────────────────

const styles = {
  container: {
    position: "relative",
    width: 320,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: colors.gray50,
    borderRadius: 8,
    padding: "8px 12px",
    border: "1px solid transparent",
    transition: "all 0.2s ease",
  },
  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 13,
    color: colors.black,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  clearBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    backgroundColor: colors.gray200,
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    color: colors.gray600,
  },
  shortcut: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    color: colors.gray400,
    backgroundColor: colors.gray200,
    borderRadius: 4,
    padding: "2px 6px",
    fontWeight: 400,
    userSelect: "none",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: 12,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
    zIndex: 300,
    overflow: "hidden",
    maxHeight: 480,
    overflowY: "auto",
  },
  categoryHeader: {
    fontSize: 11,
    fontWeight: 400,
    color: colors.gray600,
    padding: "10px 14px 4px",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  resultItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    cursor: "pointer",
    transition: "background-color 0.1s ease",
  },
  resultIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  resultInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },
  resultLabel: {
    fontSize: 13,
    fontWeight: 400,
    color: colors.black,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  resultSublabel: {
    fontSize: 11,
    color: colors.gray400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 14px",
    borderTop: `1px solid ${colors.gray100}`,
    backgroundColor: colors.gray50,
  },
  footerText: {
    fontSize: 11,
    color: colors.gray400,
  },
  footerHint: {
    fontSize: 10,
    color: colors.gray300,
  },
  noResults: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "24px 14px",
    color: colors.gray400,
    fontSize: 13,
  },
};

export default GlobalSearch;
